/**
 * String Collator
 * Wraps Intl.Collator for locale-aware string comparison and sorting
 * @module @infra/format
 */

import type { CollatorOptions } from './format.types';

const collatorsCache = new Map<string, Intl.Collator>();

/**
 * Generate cache key for collator
 */
function getCacheKey(locale: string, options?: CollatorOptions): string {
  return `${locale}:${JSON.stringify(options || {})}`;
}

/**
 * Compare two strings respecting locale rules
 * @param a - First string
 * @param b - Second string
 * @param locale - BCP 47 language tag
 * @param options - Collator options
 * @returns Number: < 0 (a < b), 0 (equal), > 0 (a > b)
 */
export function compareStrings(
  a: string,
  b: string,
  locale: string,
  options?: CollatorOptions
): number {
  const cacheKey = getCacheKey(locale, options);

  if (!collatorsCache.has(cacheKey)) {
    collatorsCache.set(cacheKey, new Intl.Collator(locale, options));
  }

  return collatorsCache.get(cacheKey)!.compare(a, b);
}

/**
 * Sort array of strings respecting locale rules
 * @param strings - Array of strings to sort
 * @param locale - BCP 47 language tag
 * @param options - Collator options
 * @returns New sorted array
 */
export function sortStrings(
  strings: string[],
  locale: string,
  options?: CollatorOptions
): string[] {
  return [...strings].sort((a, b) => compareStrings(a, b, locale, options));
}
